import {parseTimeframe} from 'components/shared/TimeAgo';
import {CURRENT_QUARTER, CURRENT_YEAR} from 'constant';

import type {MetricsGrain} from './MetricsGrainControls';
import {
  getDefaultMetricQuarter,
  METRIC_QUARTERS,
  type MetricQuarter,
} from './WorkstreamMetricsQuarterTable';

/**
 * Fixed window the metrics views expose: FY26 through FY27, inclusive. Metrics only
 * carry FY26–FY27 planning data, so the timeframe dropdown is scoped to that range
 * rather than the set of periods/years that happen to have data.
 */
export const METRICS_TIMEFRAME_START_YEAR = 2026;
export const METRICS_TIMEFRAME_END_YEAR = 2027;

/** e.g. (Q3, 2026) -> "Q3 FY26" (the value shape the timeframe menu uses). */
export const toMetricsTimeframeValue = (quarter: MetricQuarter, year: number) =>
  `${quarter} FY${String(year).slice(-2)}`;

/** Inverse of `toMetricsTimeframeValue`; returns null for anything unparseable. */
export const parseMetricsTimeframeValue = (
  value: string
): {quarter: MetricQuarter; year: number} | null => {
  const {quarterString, yearNum} = parseTimeframe(value);
  const quarter = quarterString as MetricQuarter;
  if (!METRIC_QUARTERS.includes(quarter) || Number.isNaN(yearNum)) {
    return null;
  }
  return {quarter, year: yearNum};
};

/** A Metrics view timeframe: a quarter, or (annual) a whole year. */
export type MetricsTimeframe = {grain: MetricsGrain; quarter: MetricQuarter; year: number};

/** Where the Metrics view opens: the current quarter. */
export const getDefaultMetricsTimeframe = (): MetricsTimeframe => ({
  grain: 'quarterly',
  quarter: getDefaultMetricQuarter(CURRENT_QUARTER),
  year: CURRENT_YEAR,
});

/** A Metrics timeframe as a URL value: "Q3 FY26" for a quarter, "FY26" for a year. */
export const toMetricsTimeframeParam = ({grain, quarter, year}: MetricsTimeframe) =>
  grain === 'annual' ? `FY${String(year).slice(-2)}` : toMetricsTimeframeValue(quarter, year);

/**
 * Inverse of `toMetricsTimeframeParam`. Returns null for anything unparseable or
 * outside the menu's years. A year keeps the default quarter for a later switch
 * back to Quarterly.
 */
export const parseMetricsTimeframeParam = (
  raw: string | null | undefined
): MetricsTimeframe | null => {
  const value = raw?.trim();
  if (!value) return null;
  const annual = /^FY(\d{2})$/.exec(value);
  const parsed = annual
    ? {
        grain: 'annual' as const,
        quarter: getDefaultMetricQuarter(CURRENT_QUARTER),
        year: 2000 + Number(annual[1]),
      }
    : (() => {
        const quarterly = parseMetricsTimeframeValue(value);
        return quarterly ? {grain: 'quarterly' as const, ...quarterly} : null;
      })();
  return parsed &&
    parsed.year >= METRICS_TIMEFRAME_START_YEAR &&
    parsed.year <= METRICS_TIMEFRAME_END_YEAR
    ? parsed
    : null;
};

/**
 * Quarters from Q1 2026 to Q4 2027, grouped by year (ascending), in the value shape
 * the quarterly timeframe menu uses.
 */
export const buildMetricsQuarterOptions = (): {[year: number]: string[]}[] => {
  const options: {[year: number]: string[]}[] = [];
  for (let year = METRICS_TIMEFRAME_START_YEAR; year <= METRICS_TIMEFRAME_END_YEAR; year++) {
    options.push({
      [year]: METRIC_QUARTERS.map((quarter) => toMetricsTimeframeValue(quarter, year)),
    });
  }
  return options;
};

/** Years the annual timeframe menu offers, ascending: [2026, 2027]. */
export const buildMetricsYearOptions = (): number[] => {
  const years: number[] = [];
  for (let year = METRICS_TIMEFRAME_START_YEAR; year <= METRICS_TIMEFRAME_END_YEAR; year++) {
    years.push(year);
  }
  return years;
};
